import { collectTimelineValues } from './timeline-collectors';

/**
 * Build partial lookup-table entries for timeline items that already carry
 * enough metadata to render in the playlist (name/thumbnail/type). Items
 * added from lightweight sources (e.g. Global Search) don't exist in their
 * respective lookup tables until the full metadata is fetched from the API,
 * which caused them to flash the "deleted" fallback immediately after being
 * added. Filling partial entries up front fixes that flash; the per-resource
 * fetch actions still run afterwards and overwrite the partial data with the
 * full records (the fetch actions key off fields a partial entry doesn't have,
 * like `settings`/`elements`, or the `partial` marker for polls).
 *
 * @param {Array<Object>} timeline - Production timeline (nested posts included)
 * @param {Object} rootState - Vuex root state, used to skip already-loaded entries
 * @returns {Array<{mutation: string, entry: Object}>} Partial entries to commit
 */
export const collectPartialLookupEntries = (timeline, rootState = {}) => {
  const entries = [];

  collectTimelineValues(timeline, (item) => {
    if (!item) {
      return null;
    }

    const previewUrl = item.previewUrl || item.preview_url;

    switch (item.layout) {
      case 'graphic': {
        if (!item.template_id) {
          return null;
        }

        if (rootState.graphics?.templatesLookupTable?.[item.template_id]) {
          return null;
        }

        entries.push({
          mutation: 'ADD_TEMPLATES_TO_LOOKUP_TABLE',
          entry: {
            _id: item.template_id,
            ...(item.title ? { title: item.title } : {}),
            ...(item.meta ? { meta: item.meta } : {}),
            ...(previewUrl ? { preview_url: previewUrl } : {}),
          },
        });

        break;
      }

      case 'panel': {
        if (!item._id) {
          return null;
        }

        if (rootState.panels?.lookupTable?.[item._id]) {
          return null;
        }

        entries.push({
          mutation: 'ADD_PANELS_TO_LOOKUP_TABLE',
          entry: {
            _id: item._id,
            ...(item.title ? { title: item.title } : {}),
            ...(item.type ? { type: item.type } : {}),
            ...(previewUrl ? { preview_url: previewUrl } : {}),
          },
        });

        break;
      }

      case 'poll': {
        if (!item.pollId) {
          return null;
        }

        const current = rootState.polls?.lookupTable?.[item.pollId];
        if (current && !current.partial) {
          return null;
        }

        entries.push({
          mutation: 'polls/ADD_POLLS_TO_LOOKUP_TABLE',
          entry: {
            pollId: item.pollId,
            ...(item.title ? { title: item.title } : {}),
            displays: [{
              displayId: item.displayId || item.pollId,
              ...(item.title ? { title: item.title } : {}),
              ...(item.displayType ? { displayType: item.displayType } : {}),
              ...(previewUrl ? { previewUrl } : {}),
            }],
            partial: true,
          },
        });

        break;
      }

      default: {
        // Everything else with a resource id is a theme (any non-container
        // layout), so give the playlist enough to render name/thumbnail.
        // `asset` is handled by the assets lookup, not themes.
        if (
          ['segment', 'graphics', 'marker', 'asset'].includes(item.layout)
          || !item._id
        ) {
          return null;
        }

        if (rootState.themes?.lookupTable?.[item._id]) {
          return null;
        }

        entries.push({
          mutation: 'ADD_THEMES_TO_LOOKUP_TABLE',
          entry: {
            _id: item._id,
            ...(item.title ? { title: item.title } : {}),
            ...(item.layout ? { layout: item.layout } : {}),
            ...(previewUrl ? { preview_url: previewUrl } : {}),
          },
        });

        break;
      }
    }

    return null;
  }, new Set());

  return entries;
};

export default collectPartialLookupEntries;
